import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  Injector,
  inject,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntil } from 'rxjs';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TuiDialogService } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { AlertService } from '@src/core/services';
import { Nullable } from '@src/types/utils';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { BaseComponent } from '@src/app/modules/base';

import { ContactsCustomListItem } from '../../../types';
import { HeaderOptions } from '../../header/types';
import { FooterOptions } from '../../footer/types';

import { ContactsCustomListItemForm, ContactsCustomListItemGroup } from './types';

@Component({
  selector: 'list-edit',
  templateUrl: './list-edit.component.html',
  styleUrls: ['./list-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListEditComponent extends BaseComponent implements OnChanges {
  private readonly translateService = inject(TranslateService);
  private readonly alertService = inject(AlertService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);

  @Input() set value(listItems: Nullable<Array<ContactsCustomListItem>> | undefined) {
    if (listItems && !isEqual(listItems, this.listItemsControl?.value)) {
      this.listItemsControl?.clear();
      listItems?.forEach(() => {
        this.addListItem();
      });

      this.form.patchValue({ listItems });
    }
    this.cdr.markForCheck();
  }
  @Input() headerTitle?: string;
  @Input() maxTitleLength: number = 100;
  @Input() loading = false;

  @Output() return = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<ContactsCustomListItem[]>();

  form = new FormGroup<ContactsCustomListItemForm>({
    listItems: new FormArray([this.createListItem()]), // TODO: remove initial value
  });

  headerOptions: HeaderOptions = {
    title: this.translateService.instant('components.contactsDirectory.labels.headerTitle'),
    buttons: {
      right: {
        icon: '@tui.circle-plus',
        visible: () => true,
        action: () => {
          this.addListItem();
        },
      },
    },
  };

  footerOptions: FooterOptions = {
    buttons: {
      left: {
        label: this.translateService.instant('common.buttons.save'),
        visible: () => true,
        action: () => {
          this.onClickSaveButton();
        },
      },
      right: {
        label: this.translateService.instant('common.buttons.cancel'),
        visible: () => true,
        action: () => {
          this.onClickCancelButton();
        },
      },
    },
  };

  private readonly confirmCancelEditingDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.undoEditHeader'),
      size: 's',
      closeable: false,
    },
  );

  get listItemsControl(): FormArray<FormGroup<ContactsCustomListItemGroup>> | undefined {
    return this.form?.controls?.listItems;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.headerTitle && this.headerTitle) {
      this.headerOptions.title = this.headerTitle;
    }
  }

  removeListItem(index: number): void {
    this.listItemsControl?.removeAt(index);
  }

  moveListItem(event: CdkDragDrop<string[]>) {
    if (!this.listItemsControl?.controls) return;
    moveItemInArray(this.listItemsControl.controls, event.previousIndex, event.currentIndex);
    this.listItemsControl.controls.forEach((item, index) => {
      item.patchValue({ sortOrder: index * 10 });
    });
  }

  addListItem(): void {
    this.listItemsControl?.push(this.createListItem());
  }

  createListItem(): FormGroup<ContactsCustomListItemGroup> {
    const newSortOrder = ((this.listItemsControl?.length ?? 0) + 1) * 10;

    return new FormGroup<ContactsCustomListItemGroup>({
      id: new FormControl(null),
      title: new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(this.maxTitleLength)],
      }),
      sortOrder: new FormControl(newSortOrder, { nonNullable: true }),
    });
  }

  onClickSaveButton() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.error(this.translateService.instant('common.alerts.errors.fillRequired'));
      return;
    }

    const customList = this.listItemsControl?.getRawValue().filter(listItem => !!listItem.title);
    this.save.emit(customList);
  }

  onClickCancelButton() {
    this.confirmCancelEditingDialog.pipe(takeUntil(this.destroyed$$)).subscribe(res => {
      if (res) {
        this.cancel.emit();
      }
    });
  }
}
